import {
  Badge,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  TypographyProps,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { fontSize } from '@verantos-dev/shared-ui-components/tokens';

import { Icon, IconType } from '../../Icon/Icon';
import { Tooltip } from '../../Tooltip/Tooltip';

export type SidebarItemDetail = {
  /**
   * @props {string} id - The id of the sidebar.
   */
  id: string;

  /**
   * @props { 'noIcon' | 'blank' | 'usersIcon'  | 'organizationsIcon  | 'studiesIcon'  | 'accuracyIcon'  | 'cohortsIcon'} icon

   */
  icon: 'noIcon' | ('blank' | IconType);

  /**
   * @props {string} label - The label of the sidebar item.
   * @default ''
   */
  label: string;

  /**
   * @props {string} secondaryLabel - The secondary label of the sidebar item.
   * @default ''
   */
  secondaryLabel?: string;

  /**
   * @props {boolean} sectionLabel - style the label for a section label
   * @default false
   *
   */
  sectionLabel?: boolean;

  /**
   * @props {boolean} selected - Whether the sidebar item is selected.
   * @default false
   */
  selected?: boolean;

  /**
   * @props {boolean} disabled - Whether the sidebar item is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * @props {boolean} seleactable - Whether the sidebar item is isSelectable.
   * @default true
   */
  isSelectable?: boolean;

  /**
   * @props {boolean} divider - is a divider displayed after the item
   * @default false
   */
  divider?: boolean;

  /**
   * @props {() => void} onClick - The function to call when the sidebar item is clicked.
   * @default () => {}
   */
  onClick?: () => void;

  /**
   * @props {string} href - The URL path for the sidebar item. When provided, enables Ctrl+click (Cmd+click on Mac) or Shift+click to open in new tab/window.
   * @default undefined
   */
  href?: string;

  /**
   * @props {string} tooltip - Optional tooltip text to display on hover
   * @default undefined
   */
  tooltip?: string;

  /**
   * @props {boolean} isSubPage - Whether the sidebar item is a sub-page item that should be indented.
   * @default false
   */
  isSubPage?: boolean;

  /**
   * @props {string} customTextColor - Custom text color for the item (e.g., 'primary', 'secondary', or hex color)
   * @default undefined
   */
  customTextColor?: string;

  /**
   * @props {string} customIconColor - Custom icon color for the item (e.g., 'primary', 'secondary', or hex color)
   * @default undefined
   */
  customIconColor?: string;

  /**
   * @props {boolean} isNotPageSelection - Whether this item is not a page selection (e.g., Research Assistant). Disables background and left border when selected.
   * @default false
   */
  isNotPageSelection?: boolean;

  /**
   * Icon height
   */
  iconHeight?: number;

  /**
   * Icon width
   */
  iconWidth?: number;

  /**
   * @props {React.ReactNode} badgeContent - Content to display in the badge (e.g., count, text, custom SVG, or any React element)
   * @default undefined
   */
  badgeContent?: React.ReactNode;

  /**
   * @props {boolean} showBadge - Whether to show the badge on the icon
   * @default false
   */
  showBadge?: boolean;

  /**
   * @props {object} badgeProps - Additional props to pass to the Badge component
   * @default undefined
   */
  badgeProps?: React.ComponentProps<typeof Badge>;
};

export type SidebarItemProps = SidebarItemDetail & {
  /**
   * @props {boolean} isSidebarOpen - Whether the sidebar is open.
   * @default false
   */
  isSidebarOpen?: boolean;
};

export const SidebarItem = (props: SidebarItemProps) => {
  const {
    id,
    isSidebarOpen,
    icon,
    label,
    secondaryLabel = '',
    sectionLabel = false,
    selected = false,
    disabled = false,
    isSelectable = true,
    divider = false,
    tooltip,
    isSubPage = false,
    customTextColor,
    customIconColor,
    isNotPageSelection = false,
    iconHeight,
    iconWidth,
    badgeContent,
    showBadge = false,
    badgeProps,
    onClick,
    href,
  } = props;

  const theme = useTheme();

  const sectionHeaderProps: TypographyProps<'span', { component?: 'span' }> = {
    fontWeight: 500,
    fontSize: fontSize[12],
    color: 'text.secondary',
    variant: 'body1',
    textTransform: 'uppercase',
    noWrap: true,
    component: 'span',
  };

  const secondaryLabelProps: TypographyProps<'span', { component?: 'span' }> = {
    fontWeight: 'bold',
    color: selected ? 'text.primary' : 'text.secondary',
    variant: 'body1',
    noWrap: true,
    component: 'span',
  };

  const iconElement =
    icon !== 'noIcon' && icon !== 'blank' ? (
      <Icon
        id={`${id}-sidebar-icon-${icon.replace(/[^a-zA-Z0-9]/g, '')} `}
        icon={icon}
        height={iconHeight}
        width={iconWidth}
      />
    ) : null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
    // Allow Ctrl+click (Cmd+click on Mac) or Shift+click to open in new tab/window
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      if (href) {
        // If href is provided, let the browser handle it naturally
        return;
      }
      // If no href but onClick exists, prevent default to avoid navigation
      e.preventDefault();
      return;
    }
    // Normal click - call onClick handler
    if (onClick && isSelectable && !disabled) {
      e.preventDefault();
      onClick();
    }
  };

  const renderListItem = () => (
    <ListItem
      data-testid={`${id}-sidebar-listItem`}
      key={`${id.replace(/[^a-zA-Z0-9]/g, '')}`}
      disablePadding
      role="listitem"
    >
      <ListItemButton
        component={href ? RouterLink : 'div'}
        {...(href ? { to: href } : {})}
        data-testid={`${id}-sidebar-listItemButton`}
        style={{ pointerEvents: isSelectable ? 'auto' : 'none' }}
        sx={{
          height: theme.spacing(12),
          px: 5,
          // Additional padding for sub-pages only when sidebar is open
          pl: isSubPage && isSidebarOpen ? 10 : 5,
          position: 'relative',
          textDecoration: 'none',
          color: 'inherit',
          // Add smooth transition for padding that syncs with sidebar animation
          transition: theme.transitions.create('padding-left', {
            easing: isSidebarOpen
              ? theme.transitions.easing.easeOut
              : theme.transitions.easing.easeIn,
            duration: isSidebarOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
          // Add left border for selected items (disabled for non-page selections)
          '&::after':
            selected && !isNotPageSelection
              ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '3px',
                  backgroundColor: theme.palette.primary.main,
                }
              : {},
        }}
        disabled={disabled}
        selected={selected && !isNotPageSelection} // Disable default background for non-page selections
        role={href ? 'link' : 'button'}
        onClick={handleClick}
      >
        {iconElement && (
          <ListItemIcon
            data-testid={`${id}-sidebar-listItemIcon-${icon.replace(/[^a-zA-Z0-9]/g, '')}`}
            sx={{
              mr: isSidebarOpen ? 4 : 'auto',
              minWidth: 0,
              justifyContent: 'center',
              // Apply custom icon color if provided
              ...(customIconColor && {
                color:
                  customIconColor === 'primary'
                    ? theme.palette.primary.main
                    : customIconColor === 'secondary'
                    ? theme.palette.secondary.main
                    : customIconColor,
              }),
            }}
          >
            {showBadge ? (
              <Badge badgeContent={badgeContent} color="primary" {...badgeProps}>
                {iconElement}
              </Badge>
            ) : (
              iconElement
            )}
          </ListItemIcon>
        )}
        <ListItemText
          data-testid={`${id}-sidebar-text-${label.replace(/[^a-zA-Z0-9]/g, '')}`}
          primary={label}
          secondary={secondaryLabel}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            opacity: isSidebarOpen ? 1 : 0,
            ml: sectionLabel ? 10 : 0,
            '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
              display: 'inline',
              marginRight: label ? '8px' : 'none', // Adjust the spacing between the primary and secondary text as needed
            },
          }}
          primaryTypographyProps={{
            fontWeight: selected ? 700 : 'normal',
            color: customTextColor
              ? customTextColor === 'primary'
                ? theme.palette.primary.main
                : customTextColor === 'secondary'
                ? theme.palette.secondary.main
                : customTextColor
              : 'text.primary',
            variant: 'body1',
            noWrap: true,
          }}
          disableTypography={false}
          secondaryTypographyProps={sectionLabel ? sectionHeaderProps : secondaryLabelProps}
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <>
      {tooltip ? (
        <Tooltip id={`${id}-tooltip`} title={tooltip} placement="right" arrow>
          {renderListItem()}
        </Tooltip>
      ) : (
        renderListItem()
      )}
      {/* This is 100% width */}
      {divider && <Divider sx={{ width: '100%' }} />}
    </>
  );
};
